// src/services/api.ts
import axios, { AxiosInstance } from 'axios';
import { API_ENDPOINT as API_BASE_URL } from '../config/api';
import { logger } from '../utils/logger';

// Import all types from consolidated location
import {
  DeputyInfo,
  DeputyProfile,
  DeputyComparison,
  DeputyKeywordConsistency,
  DeputyVotingProfile,
  TopicStats,
  VoteSummary,
  DeputyVoteDetail,
  DeputyVoteInGroup,
  GroupedVoteDetail,
  VoteCountBreakdown,
  Topic,
  Subtopic,
  Keyword,
  SubtopicKeyword,
  TopicAnalysis,
  HierarchySearchResults,
  ApiResponse,
  PaginatedResponse,
} from '../types/api';
import { LegislativePeriod, CampaignPromise, PromiseStats, DeputyPromisesResponse } from '../types';

// Re-export for backward compatibility (will be removed in next refactor)
export * from '../types/api';

// Legacy interfaces that need mapping
export interface ComparativeAnalysis {
  deputies: Array<{ id: number; name: string; party: string }>;
  agreement_matrix: number[][];
  topic_alignment: Record<string, number[]>;
  voting_patterns: Array<{ vote_id: string; votes: string[] }>;
}

export interface GroupedVote {
  boletin: string;
  short_title: string;
  one_line_description?: string;
  citizen_summary?: string;
  materia: string;
  main_topic: string;
  subtopic?: string;
  keywords?: string[];
  first_vote_date: string;
  last_vote_date: string;
  vote_count: number;
  overall_result: string;
  is_controversial: boolean;
  party_votes: Record<string, {
    afirmativo: number;
    negativo: number;
    abstencion: number;
    ausente: number;
    pareo: number;
    no_vota?: number;
    dispensado?: number;
  }>;
  deputy_specific_votes?: Record<number, DeputyVoteInGroup>;
}

// Create axios instance

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      logger.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request was made but no response
      logger.error('Network Error:', error.request);
    } else {
      // Something else happened
      logger.error('Error:', error.message);
    }
    return Promise.reject(error);
  },
);

// API Service Class
export class CamaraAPI {
  // Deputy endpoints
  async getDeputies(
    includeMetrics: boolean = false,
    limit?: number,
    periodo?: string,
  ): Promise<DeputyInfo[]> {
    const params: any = {};
    if (includeMetrics) {params.include_metrics = true;}
    if (limit) {params.limit = limit;}
    if (periodo) {params.periodo = periodo;}
    const response = await api.get('/deputies', { params });
    return response.data;
  }

  async getDeputyProfile(id: number): Promise<DeputyProfile> {
    const response = await api.get(`/deputies/${id}`); // FIXED: removed /profile
    return response.data;
  }

  async getDeputyVotes(
    id: number,
    limit?: number,
  ): Promise<DeputyVoteDetail[]> {
    const params = limit ? { limit } : {};
    const response = await api.get(`/deputies/${id}/votes/grouped`, { params });
    return response.data;
  }

  // Vote endpoints
  async getMotions(limit?: number): Promise<any[]> {
    const params = limit ? { limit } : {};
    const response = await api.get('/motions', { params });
    return response.data;
  }

  async getMotionVotes(boletin: string): Promise<any[]> {
    const response = await api.get(`/motions/${boletin}/votes`);
    return response.data;
  }

  async searchVotes(query: string, topic?: string, limit: number = 50, periodo?: string): Promise<VoteSummary[]> {
    const params: any = { q: query, topic, limit };
    if (periodo) {params.periodo = periodo;}
    const response = await api.get('/votes/search', { params });
    return response.data;
  }

  async getControversialVotes(limit: number = 20, periodo?: string): Promise<VoteSummary[]> {
    const params: any = { limit };
    if (periodo) {params.periodo = periodo;}
    const response = await api.get('/votes/controversial', { params });
    return response.data;
  }

  async getVoteDetails(voteId: string): Promise<any> {
    const response = await api.get(`/votes/${voteId}`);
    return response.data;
  }

  // Topic endpoints
  async getTopics(periodo?: string): Promise<Topic[]> {
    const params: any = {};
    if (periodo) {params.periodo = periodo;}
    const response = await api.get('/topics', { params });
    return response.data;
  }

  async getSubtopics(topicId: number, periodo?: string): Promise<Subtopic[]> {
    const params: any = {};
    if (periodo) {params.periodo = periodo;}
    const response = await api.get(`/topics/${topicId}/subtopics`, { params });
    return response.data;
  }

  async getSubtopicKeywords(subtopicId: number): Promise<SubtopicKeyword[]> {
    const response = await api.get(`/subtopics/${subtopicId}/keywords`);
    return response.data;
  }

  async getKeywords(subtopicId: number): Promise<SubtopicKeyword[]> {
    return this.getSubtopicKeywords(subtopicId);
  }

  async getKeywordVotes(keyword: string, limit: number = 100): Promise<VoteSummary[]> {
    const response = await api.get(`/keywords/${encodeURIComponent(keyword)}/votes`, {
      params: { limit },
    });
    return response.data;
  }

  async getKeywordDeputyConsistency(
    keyword: string,
    deputyIds?: number[],
  ): Promise<DeputyKeywordConsistency[]> {
    const params = deputyIds ? { deputy_ids: deputyIds.join(',') } : {};
    const response = await api.get(
      `/keywords/${encodeURIComponent(keyword)}/deputy-consistency`,
      { params },
    );
    return response.data;
  }

  async getDeputyKeywordConsistency(
    keyword: string,
    deputyIds?: number[],
  ): Promise<DeputyKeywordConsistency[]> {
    return this.getKeywordDeputyConsistency(keyword, deputyIds);
  }

  async compareDeputiesOnKeyword(
    keyword: string,
    deputyIds: number[],
  ): Promise<DeputyComparison> {
    const response = await api.post(
      `/keywords/${encodeURIComponent(keyword)}/deputy-comparison`,
      { deputy_ids: deputyIds },
    );
    return response.data;
  }

  async searchHierarchy(query: string): Promise<HierarchySearchResults> {
    const response = await api.get('/topic-explorer/search', {
      params: { q: query },
    });
    return response.data;
  }

  // Topic analysis endpoints
  async getTopicAnalysis(topic: string): Promise<TopicAnalysis> {
    const response = await api.get(`/topics/${encodeURIComponent(topic)}/analysis`);
    return response.data;
  }

  // Comparison endpoints
  async compareDeputies(deputyIds: number[], topic?: string): Promise<ComparativeAnalysis> {
    const params: any = { deputy_ids: deputyIds.join(',') };
    if (topic) {params.topic = topic;}
    const response = await api.get('/deputies/compare', { params });
    return response.data;
  }

  async searchTopics(query: string, limit?: number): Promise<{
    topics: any[];
    subtopics: any[];
    keywords: any[];
    motions: any[];
  }> {
    // Note: Backend doesn't have unified search, return empty for now
    // This could be implemented by calling multiple endpoints if needed
    return {
      topics: [],
      subtopics: [],
      keywords: [],
      motions: [],
    };
  }

  // Grouped votes endpoint
  async getGroupedVotes(
    topic: string,
    subtopic: string | string[],
    deputyIds?: number[],
    limit: number = 50,
  ): Promise<GroupedVote[]> {
    // FIX: Handle subtopic properly - convert array to string if needed
    let subtopicParam: string;

    if (Array.isArray(subtopic)) {
      // If it's an array, take the first element
      subtopicParam = subtopic[0] || '';
      logger.debug('Converting subtopic array to string:', subtopic, '->', subtopicParam);
    } else if (typeof subtopic === 'string') {
      // If it's already a string, use it directly
      subtopicParam = subtopic;
    } else {
      // Fallback
      subtopicParam = '';
    }

    const params: any = {
      topic: topic,
      subtopic: subtopicParam, // Always send as string
      limit: limit,
    };

    if (deputyIds && deputyIds.length > 0) {
      params.deputy_ids = deputyIds.join(',');
    }

    logger.debug('Fetching grouped votes with params:', params);

    const response = await api.get('/grouped_votes', { params });
    return response.data;
  }

  async getDeputyVotesGrouped(
    deputyId: number,
    topic?: string,
    limit: number = 100,
  ): Promise<any[]> {
    const params: any = { limit };
    if (topic) {
      params.topic = topic;
    }

    const response = await api.get(`/deputies/${deputyId}/votes/grouped`, { params });
    return response.data;
  }

  // Period endpoints
  async getPeriods(): Promise<LegislativePeriod[]> {
    const response = await api.get('/periods');
    return response.data;
  }

  async getCurrentPeriod(): Promise<LegislativePeriod> {
    const response = await api.get('/periods/current');
    return response.data;
  }

  // Promise endpoints
  async getPromises(params?: {
    period?: string;
    status?: string;
    category?: string;
    deputy_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<CampaignPromise[]> {
    const response = await api.get('/promises', { params });
    return response.data;
  }

  async getPromiseStats(period?: string): Promise<PromiseStats> {
    const params = period ? { period } : {};
    const response = await api.get('/promises/stats', { params });
    return response.data;
  }

  async getDeputyPromises(deputyId: number, periodo?: string): Promise<DeputyPromisesResponse> {
    const params = periodo ? { periodo } : {};
    const response = await api.get(`/deputies/${deputyId}/promises`, { params });
    return response.data;
  }

  // Stats endpoint
  async getSummaryStats(periodo?: string): Promise<{
    total_deputies: number;
    total_votes: number;
    average_attendance: number;
    top_topics: Array<{ topic: string; count: number }>;
    last_updated?: string;
  }> {
    const params: any = {};
    if (periodo) {params.periodo = periodo;}
    const response = await api.get('/stats', { params }); // FIXED: /stats instead of /stats/overview
    return response.data;
  }

  async getOverviewStats(): Promise<{
    total_deputies: number;
    total_votes: number;
    total_projects: number;
    average_attendance: number;
    ai_analysis_coverage: number;
    top_topics: Array<{ topic: string; count: number }>;
  }> {
    const response = await api.get('/stats');
    // Map backend response to expected format
    const data = response.data;
    return {
      total_deputies: data.total_deputies || 0,
      total_votes: data.total_votes || 0,
      total_projects: data.total_projects || 0,
      average_attendance: data.average_attendance || 0,
      ai_analysis_coverage: data.ai_analysis_coverage || 0,
      top_topics: data.top_topics || [],
    };
  }
}



// Create and export a singleton instance
export const camaraAPI = new CamaraAPI();

// Export endpoints object for direct usage if preferred
export const endpoints = {
  deputies: {
    list: (params?: any) => api.get('/deputies', { params }),
    profile: (id: number) => api.get(`/deputies/${id}`), // FIXED: removed /profile
    votes: (id: number, params?: any) => api.get(`/deputies/${id}/votes/grouped`, { params }),
  },
  votes: {
    search: (params: { q: string; topic?: string; limit?: number }) =>
      api.get('/votes/search', { params }),
    controversial: (limit?: number) =>
      api.get('/votes/controversial', { params: { limit } }),
    details: (voteId: string) => api.get(`/votes/${voteId}`),
  },
  topics: {
    list: () => api.get('/topics'),
    analysis: (topic: string) => api.get(`/topics/${encodeURIComponent(topic)}/analysis`),
    subtopics: (topicId: number) => api.get(`/topics/${topicId}/subtopics`),
  },
  keywords: {
    bySubtopic: (subtopicId: number) => api.get(`/subtopics/${subtopicId}/keywords`),
    votes: (keyword: string, limit?: number) =>
      api.get(`/keywords/${encodeURIComponent(keyword)}/votes`, { params: { limit } }),
    deputyConsistency: (keyword: string, deputyIds?: number[]) =>
      api.get(`/keywords/${encodeURIComponent(keyword)}/deputy-consistency`, {
        params: deputyIds ? { deputy_ids: deputyIds.join(',') } : {},
      }),
    comparison: (keyword: string, deputyIds: number[]) =>
      api.post(`/keywords/${encodeURIComponent(keyword)}/deputy-comparison`, { deputy_ids: deputyIds }),
  },
  search: {
    hierarchy: (query: string) => api.get('/topic-explorer/search', { params: { q: query } }),
  },
  compare: {
    deputies: (deputyIds: number[], topic?: string) => {
      const params: any = { deputy_ids: deputyIds.join(',') };
      if (topic) {params.topic = topic;}
      return api.get('/deputies/compare', { params });
    },
  },
  stats: {
    summary: () => api.get('/stats'), // FIXED: /stats instead of /stats/overview
  },
  groupedVotes: {
    list: (topic: string, subtopic: string | string[], deputyIds?: number[], limit?: number) => {
      // Handle subtopic conversion
      const subtopicParam = Array.isArray(subtopic) ? subtopic[0] : subtopic;

      const params: any = {
        topic,
        subtopic: subtopicParam,
        limit: limit || 50,
      };

      if (deputyIds && deputyIds.length > 0) {
        params.deputy_ids = deputyIds.join(',');
      }

      return api.get('/grouped_votes', { params });
    },
    byDeputy: (deputyId: number, topic?: string, limit?: number) => {
      const params: any = { limit: limit || 100 };
      if (topic) {params.topic = topic;}
      return api.get(`/deputies/${deputyId}/votes/grouped`, { params });
    },
  },
};

// Export default api instance for backward compatibility
export default api;
